import { DATE_FORMAT } from 'config';
import { IconButton, InputAdornment, Popover, type SxProps, Tooltip, styled } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar, type DateCalendarProps } from '@mui/x-date-pickers/DateCalendar';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useEffect, useMemo, useState } from 'react';
import { ComboBox } from 'components/inputs';
import { Calendar, X } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import * as chrono from 'chrono-node';
import type { Dayjs } from 'dayjs';
import type { ComboBoxOption, CommonLabelSeparateProps } from 'components/inputs/types';
import { useTheme } from '@emotion/react';
import { t } from '@lingui/macro';

export type DatePickerProps = CommonLabelSeparateProps & {
  formatErrorHelperText?: string;
  id: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  onChange?: (
    event: React.SyntheticEvent,
    date: Dayjs | null,
    reason: string,
    stringDate: string,
    error: boolean
  ) => void;
  onBlur?: (date: Dayjs | null, stringDate: string, error: boolean) => void;
  inputVariant?: 'outlined' | 'standard' | 'filled';
  initialDate?: string;
  options?: ComboBoxOption[];
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }
  ) => void;
  sx?: SxProps;
  hideClear?: boolean;
  isStopPropagationOnKeyDown?: boolean;
  disabled?: boolean;
  shouldDisableDate?: DateCalendarProps<Dayjs>['shouldDisableDate'];
};

const calendarColor = '#B3B3B3';

export const getDateFromValue = (str: string | undefined) => {
  if (!str) return null;
  const naturalDate = chrono.parseDate(str);
  const dayDate = dayjs(naturalDate);
  return dayDate?.isValid() ? dayDate : null;
};

export const DatePicker = ({
  id,
  placeholder,
  onChange,
  onBlur,
  options = [],
  inputVariant,
  initialDate = '',
  sx,
  onKeyDown,
  hideClear,
  isStopPropagationOnKeyDown,
  formatErrorHelperText = t`Invalid date format`,
  helperText,
  error: propsError,
  shouldDisableDate,
  ...rest
}: DatePickerProps) => {
  const [inputValue, setInputValue] = useState<string | null>(initialDate);
  const [date, setDate] = useState<dayjs.Dayjs | null>(getDateFromValue(initialDate));
  const [error, setError] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [dateDescription, setDateDescription] = useState<string>(initialDate);
  const dateFormat = 'DD-MMM-YYYY';
  const theme = useTheme();

  const handleOnChange = (
    event: React.SyntheticEvent,
    value: ComboBoxOption | null | string | ComboBoxOption[],
    reason: string,
    stringDate?: string
  ) => {
    if (Array.isArray(value)) return;
    if (value === null) return;
    const dateString = typeof value === 'string' ? value : value?.label;
    const newDate = getDateFromValue(dateString);
    setDateDescription(dateString);
    if (reason === 'selectOption') {
      if (!newDate) {
        setError(true);
      }
      setDate(newDate);
      setError(false);
    }

    if (reason === 'clear' || reason === 'reset') {
      setError(false);
    }

    onChange && onChange(event, newDate, reason, dateString, error);
  };

  const handleBlur = () => {
    const newDate = getDateFromValue(dateDescription || '');
    setInputValue(dateDescription);
    setDate(newDate);

    const isValidDate = newDate ? newDate.isValid() : false;
    if (!isValidDate && dateDescription) {
      setError(true);
    } else {
      setError(false);
    }
    if (!anchorEl) {
      onBlur?.(newDate, dateDescription, isValidDate);
    }
  };

  const handleCalendarChange = (newDate: Dayjs) => {
    setDate(newDate);
    setInputValue(newDate.format(dateFormat));
    handleCloseCalendar();
  };

  const handleCalendarToggle = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    setAnchorEl(null);
  };

  const handleClearInput = () => {
    setInputValue('');
    setError(false);
    setDate(null);
    onBlur?.(null, '', false);
    onChange?.(
      { target: { value: undefined } } as unknown as React.ChangeEvent<HTMLInputElement>,
      null,
      'onClear',
      '',
      false
    );
  };
  const onkeydown: DatePickerProps['onKeyDown'] = event => {
    if (isStopPropagationOnKeyDown) {
      event.stopPropagation();
    }
    if (event.key === 'Enter') {
      handleBlur();
    }
    if (onKeyDown) onKeyDown(event);
  };

  const dateValue = useMemo(() => {
    return inputValue ? getDateFromValue(inputValue)?.format(dateFormat) ?? '' : '';
  }, [inputValue, dateFormat]);

  useEffect(() => {
    if (!inputValue && initialDate && inputValue !== null) {
      setInputValue(initialDate);
    }
  }, [initialDate]);

  return (
    <Container>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ComboBox
          {...rest}
          sx={{ ...sx, paddingRight: '0' }}
          value={dateValue}
          freeSolo
          error={error || propsError}
          helperText={error ? formatErrorHelperText : propsError ? helperText : ''}
          id="date-picker"
          placeholder={placeholder}
          options={options}
          onChange={(event, value, reason) => handleOnChange(event, value, reason)}
          onInputChange={(event, value, reason) => handleOnChange(event, value, reason)}
          onKeyDown={event => onkeydown(event)}
          inputVariant={inputVariant}
          clearOnBlur={false}
          onBlur={() => handleBlur()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {inputValue && !hideClear && (
                  <IconButton
                    className="clear-input"
                    aria-label="clear input"
                    onClick={handleClearInput}
                    onKeyDown={handleClearInput}
                  >
                    <X size={20} />
                  </IconButton>
                )}
                <IconButton
                  aria-label="open up calendar"
                  edge="end"
                  onClick={handleCalendarToggle}
                  onKeyDown={handleCalendarToggle}
                >
                  <Tooltip title={date?.isValid() && date?.format(DATE_FORMAT)}>
                    <Calendar
                      color={error ? theme.palette.border.error : calendarColor}
                      style={{ minWidth: 20 }}
                      size={20}
                    />
                  </Tooltip>
                </IconButton>
                <Popover
                  data-testid="calendar-popover"
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleCloseCalendar}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                >
                  <DateCalendar
                    shouldDisableDate={shouldDisableDate}
                    value={date}
                    onChange={handleCalendarChange}
                  />
                </Popover>
              </InputAdornment>
            ),
          }}
        />
      </LocalizationProvider>
    </Container>
  );
};

const Container = styled('div')`
  width: 100%;
  .clear-input {
    pointer-events: none;
    opacity: 0;
  }

  &:focus-within,
  &:hover {
    .clear-input {
      pointer-events: auto;
      opacity: 1;
    }
  }

  &&& .MuiInputBase-adornedEnd {
    padding-right: 20px;
  }
`;
